// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import FancyScroll, { FancyScrollRefProps } from "@flip/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@flip/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@flip/App/Components/MiniCart/MiniCartLine";
import Notify from "@flip/App/Components/Notify/Notify";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { xIsAuthenticated } from "@flip/App/Store/Selectors/GeneralSelectors";
import Tooltip from "@flip/MobiusOverrides/Tooltip/Tooltip";
import { styledScrollbar } from "@flip/XDL/Utilities";
import color from "@flip/XDL/Utilities/color";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import {
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canCheckoutWithCart } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getCountriesDataView } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import { loadCurrentCountriesParameter } from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export const cartLineTippyContentTopPadding = 20;

interface OwnProps {
    isPopupShown?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    // XNG-Changes
    const cartState = getCurrentCartState(state);
    const cart = cartState.value;
    const currentPage = getCurrentPage(state);
    const countriesDataView = getCountriesDataView(state, loadCurrentCountriesParameter);

    // XNG-Changes
    const { isUpdatingCart } = state.context;

    return {
        canCheckoutWithCart: canCheckoutWithCart(cart),
        canSubmitRequisition: canSubmitRequisition(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        shouldLoadCart:
            !getCurrentCartState(state).value &&
            !getCurrentCartState(state).isLoading &&
            currentPage.type !== "CartPage",
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        cart,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        isLoading: state.data.carts.isLoading,
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        isCartCheckoutDisabled: cartState.isLoading || !cart || isCartCheckoutDisabled(cart) || isUpdatingCart,
        hasOnlyQuoteRequiredProducts: hasOnlyQuoteRequiredProducts(cart),
        isPreloadingData: state.pages.onePageCheckoutConfirmation.isPreloadingData,
        isLoadingCountries: countriesDataView.isLoading,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    tippyTitle?: TypographyPresentationProps;
    tippyContainer?: InjectableCss;
    title?: TypographyProps;
    noCartLinesNotify?: TypographyProps;
    cartLinesContainer?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    subTotalAndactionsWrapper?: InjectableCss;
    countSubtotalWrapper?: TypographyPresentationProps;
    subtotal?: TypographyPresentationProps;
    actionWrapper?: InjectableCss;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    minOrderRquirment?: InjectableCss;
    minOrderRquirmentMsg?: TypographyProps;
}

const miniCartStyles: MiniCartStyles = {
    tippyTitle: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    tippyContainer: {
        css: css`
            width: 470px;
            max-height: 65vh;
            min-height: 130px;
            padding: 20px 16px;
        `,
    },
    noCartLinesNotify: {
        css: css`
            margin: 22px 0 0;
        `,
    },
    cartLinesContainer: {
        css: css`
            padding: 20px 0 20px 16px;
            width: 470px;
        `,
    },
    cartLinesWrapper: {
        css: css`
            position: relative;
            min-height: 100px;
            max-height: calc(100vh - 330px);
            padding-right: 8px;

            & > div:last-child {
                margin-bottom: 10px;
            }
            overflow-x: hidden;
            ${styledScrollbar(8)}
        `,
    },
    subTotalAndactionsWrapper: {
        css: css`
            padding: 20px 16px 16px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    countSubtotalWrapper: {
        css: css`
            margin-top: 12px;
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
        `,
    },
    subtotal: {
        variant: "h4",
        css: css`
            display: inline;
            margin-left: 8px;
            margin-bottom: 0;
        `,
    },
    actionWrapper: {
        css: css`
            margin-top: 12px;
            display: flex;
            justify-content: space-between;
        `,
    },
    checkoutButton: {
        sizeVariant: "large",
        mergeCss: true,
    },
    viewCartButton: {
        variant: "secondary",
        sizeVariant: "large",
        mergeCss: true,
    },
    minOrderRquirment: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    minOrderRquirmentMsg: {
        forwardAs: "p",
        color: "danger",
        size: 14,
        lineHeight: "22px",
    },
};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    cart,
    cartPageUrl,
    requisitionConfirmationPageUrl,
    isPunchOutOrder,
    canCheckoutWithCart,
    removeCartLine,
    updateCartLine,
    submitRequisition,
    preloadCheckoutShippingData,
    canSubmitRequisition,
    doPunchOutCheckout,
    isUpdatingCartLine,
    isRemovingCartLine,
    isLoading,
    checkoutShippingPageUrl,
    isPopupShown,
    totalCountDisplay,
    orderSubTotalDisplay,
    isCartCheckoutDisabled,
    hasOnlyQuoteRequiredProducts,
    isPreloadingData,
    isLoadingCountries,
}) => {
    const history = useHistory();

    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }

        preloadCheckoutShippingData({
            onSuccess: () => {
                history.push(checkoutShippingPageUrl!);
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        hideTippy();
        history.push(cartPageUrl);
    };

    if (!cart) {
        return null;
    }

    const cartLines = cart.cartLines ?? [];
    const isLoadingCart = isLoading[cart.id];

    if (!cartLines?.length) {
        return (
            <StyledWrapper {...styles.tippyContainer}>
                <Typography {...styles.tippyTitle}>{translate("Cart Summary")}</Typography>
                <Notify {...styles.noCartLinesNotify} type="warning" showClose={false}>
                    {siteMessage("FLIP_Mini_Cart_No_Items")}
                </Notify>
            </StyledWrapper>
        );
    }

    return (
        <LoadingOverlay
            {...styles.loadingOverlay}
            loading={
                isLoadingCart || isUpdatingCartLine || isRemovingCartLine || isPreloadingData || isLoadingCountries
            }
        >
            <StyledWrapper {...styles.cartLinesContainer}>
                <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                    <StyledWrapper
                        {...styles.cartLinesWrapper}
                        ref={parentBoxRef}
                        data-test-selector="cartLines-container"
                    >
                        {cartLines?.map(cartLine => {
                            const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                            return (
                                <MiniCartLineCard
                                    key={`miniCartLine_${cartLine.id}`}
                                    cartLine={cartLine}
                                    cart={cart}
                                    hideTippy={hideTippy}
                                    qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                    removeCartLineClickHandler={removeCartLineClickHandler}
                                    errorMessages={errorMessages}
                                />
                            );
                        })}
                    </StyledWrapper>
                </FancyScroll>
            </StyledWrapper>
            <StyledWrapper {...styles.subTotalAndactionsWrapper}>
                {/** XNG-Changes: FLIP-230 */}
                {cart.properties.orderTotalLessThanMinimumOrderAmount === "true" && (
                    <StyledWrapper {...styles.minOrderRquirment}>
                        <Typography {...styles.minOrderRquirmentMsg}>
                            {siteMessage("FLIP_Cart_Min_Order_Requirment_Msg")}
                        </Typography>
                        <Tooltip
                            text={siteMessage("FLIP_Cart_Min_Order_Requirment_Tooltip_Msg")}
                            iconProps={{ size: 13, color: "common.text" }}
                        />
                    </StyledWrapper>
                )}
                {/* XNG-Changes: FLIP-858 */}
                <Typography {...styles.countSubtotalWrapper}>
                    <Typography size={16} lineHeight="26px">
                        {totalCountDisplay}&nbsp;
                        {translate(`Item${totalCountDisplay === 1 ? "" : "s"}`)}
                    </Typography>
                    <Typography>
                        {translate("Subtotal")}:<Typography {...styles.subtotal}>{orderSubTotalDisplay}</Typography>
                    </Typography>
                </Typography>
                <StyledWrapper {...styles.actionWrapper}>
                    <Button {...styles.viewCartButton} onClick={handleViewCarClick} aria-label="View Cart Summary">
                        {translate("View Cart")}
                    </Button>
                    {!canSubmitRequisition ? (
                        <Button
                            {...styles.checkoutButton}
                            onClick={checkoutHandler}
                            disabled={
                                isCartCheckoutDisabled ||
                                hasOnlyQuoteRequiredProducts ||
                                !canCheckoutWithCart ||
                                isPreloadingData ||
                                isLoadingCountries
                            }
                            aria-label={
                                cart?.requiresApproval
                                    ? translate("Checkout for Approval")
                                    : isPunchOutOrder
                                    ? translate("Return to Procurement System")
                                    : translate("Continue to Checkout")
                            }
                        >
                            {cart?.requiresApproval
                                ? translate("Checkout for Approval")
                                : isPunchOutOrder
                                ? translate("Return to Procurement System")
                                : translate("Continue to Checkout")}
                        </Button>
                    ) : (
                        <Button
                            {...styles.checkoutButton}
                            onClick={submitRequisitionClickHandler}
                            data-test-selector="cartTotal_SubmitRequisition"
                            aria-label="Submit Requisition"
                        >
                            {translate("Submit Requisition")}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
